import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import { GraphQLClientService } from '../../../../../core/services/graphql-client.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import { deriveBrandTokens, textOnColor } from '../../../../../core/utils/brand-color';
import { PaperIconComponent } from '@ricoh-paper/angular';

/** Derived dark/light-safe colour tokens for a meeting label. */
export interface MeetingLabelTokens {
  dark: string;
  light: string;
  bgDark: string;
  bgLight: string;
  borderDark: string;
  borderLight: string;
  textOnDark: string;
  textOnLight: string;
}

/** Booking-panel option shape — combines the persisted MeetingLabel with
 *  pre-computed tokens so the template can render without re-deriving
 *  on every change-detection cycle. */
export interface MeetingLabelOption {
  _id: string;
  name: string;
  color: string | null;
  tokens: MeetingLabelTokens | null;
}

/**
 * MoreOptionsSectionComponent — collapsible section for secondary booking fields.
 *
 * RESPONSIBILITY: Description textarea, cost centre dropdown (fetched from
 * bookingCapabilities API), meeting label dropdown, and visibility dropdown.
 * Auto-opens when catering turns on and cost centre is empty.
 *
 * SIGNALS (from BookMeetingService):
 *   description, costCentre, meetingLabel, visibility, hasCatering
 *
 * USED BY: BookMeetingPanelComponent (form view)
 * BACKEND CONTRACT: bookingCapabilities(spaceId) → costCentreMandatory, costCentreValues
 */

const BOOKING_CAPABILITIES_QUERY = `
  query bookingCapabilities($spaceId: ID!, $date: String!) {
    bookingCapabilities(spaceId: $spaceId, date: $date) {
      costCentreMandatory
      costCentreValues
      canRecur
      allowRecurring
      maxRecurringOccurrences
      maxRecurringDaysAhead
    }
  }
`;

@Component({
  selector: 'im-more-options-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, TranslocoPipe],
  template: `
    <!-- Schedule V2 — "More scheduling options" lives in the v2-gutter
         section grid so the chevron + meta line up with the rest of the
         Options tab. The trigger is the first row; the body card hangs
         below it inside the same column. Aria: trigger uses
         aria-expanded/-controls; body is a role=region so screen readers
         announce it on disclosure. -->
    <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter bmp__mos-wrap">
      <span class="bmp__section-icon" aria-hidden="true">
        <paper-icon name="sliders" [size]="14" />
      </span>
      <div class="mos">
        <button
          type="button"
          class="bmp__mos-trigger mos__toggle"
          (click)="expanded.set(!expanded())"
          [attr.aria-expanded]="expanded()"
          aria-controls="bmp-mos-body">
          <paper-icon
            class="mos__chevron"
            [class.expanded]="expanded()"
            name="chevron-down"
            [size]="12"
            aria-hidden="true" />
          <span class="mos__toggle-label">{{ 'viewer.book_meeting_panel.more_options.title' | transloco }}</span>
          <span class="bmp__mos-meta">{{ summaryMeta() }}</span>

          @if (showCostCentreWarning() && !readonly()) {
            <span class="mos__warning-pill">{{ 'viewer.book_meeting_panel.more_options.cost_centre_required' | transloco }}</span>
          }
        </button>

      @if (expanded()) {
        <div id="bmp-mos-body" role="region" [attr.aria-label]="'viewer.book_meeting_panel.more_options.title' | transloco" class="bmp__mos-body mos__content">

          <!-- Agenda lives in the Briefing tab (im-notes-section); the
               duplicate that used to render here has been removed. The
               briefing tab is the single source of truth for the
               public agenda HTML. -->

          <!-- 2-column grid for dropdowns -->
          <div class="bmp__mos-grid mos__grid">

            <!-- Cost Centre -->
            <div class="bmp__mos-field mos__field" [class.mos__field--warn]="showCostCentreWarning() && !readonly()">
              <label class="bmp__mos-field-label mos__label" for="mos-cost-centre"
                [class.mos__label--warn]="showCostCentreWarning() && !readonly()">
                {{ 'viewer.book_meeting_panel.more_options.cost_centre' | transloco }}
              </label>
              @if (readonly()) {
                @if (service.costCentre()) {
                  <span class="mos__readonly-value">{{ service.costCentre() }}</span>
                } @else {
                  <span class="mos__readonly-empty">{{ 'viewer.book_meeting_panel.more_options.not_set' | transloco }}</span>
                }
              } @else if (costCentreValues().length > 0) {
                <select
                  id="mos-cost-centre"
                  class="mos__select"
                  [class.mos__select--warn]="showCostCentreWarning()"
                  [value]="service.costCentre()"
                  (change)="service.costCentre.set($any($event.target).value)">
                  <option value="">{{ service.hasCatering() ? selectCostCentreLabel() : noneLabel() }}</option>
                  @for (cc of costCentreValues(); track cc) {
                    <option [value]="cc">{{ cc }}</option>
                  }
                </select>
              } @else {
                <input
                  type="text"
                  id="mos-cost-centre"
                  class="mos__select"
                  [class.mos__select--warn]="showCostCentreWarning()"
                  [placeholder]="'viewer.book_meeting_panel.more_options.cost_centre_placeholder' | transloco"
                  [value]="service.costCentre()"
                  (input)="service.costCentre.set($any($event.target).value)" />
              }
              @if (showCostCentreWarning() && !readonly()) {
                <span class="mos__hint mos__hint--warn">
                  {{ 'viewer.book_meeting_panel.more_options.cost_centre_required_hint' | transloco }}
                </span>
              }
            </div>

            <!-- Visibility -->
            <div class="bmp__mos-field mos__field">
              <label class="bmp__mos-field-label mos__label" for="mos-visibility">{{ 'viewer.book_meeting_panel.more_options.visibility' | transloco }}</label>
              @if (readonly()) {
                <span class="mos__readonly-value">{{ service.visibility() }}</span>
              } @else {
                <select
                  id="mos-visibility"
                  class="mos__select"
                  [value]="service.visibility()"
                  (change)="onVisibilityChange($any($event.target).value)">
                  <option value="Public">{{ 'viewer.book_meeting_panel.more_options.visibility_public' | transloco }}</option>
                  <option value="Private">{{ 'viewer.book_meeting_panel.more_options.visibility_private' | transloco }}</option>
                </select>
              }
            </div>
          </div>

          <!-- LABEL pill row. Active pill gets a sunken background + bold
               weight; coloured dots map to design tokens. Only
               Location.meetingLabels (j8q.11) — an un-provisioned location
               shows just the always-present "None" choice, honestly, rather
               than an invented taxonomy. Keyboard nav: role=radio focus
               cycle handles arrow movement natively. -->
          <div class="bmp__mos-field mos__field">
            <span class="bmp__mos-field-label mos__label">{{ 'viewer.book_meeting_panel.more_options.label' | transloco }}</span>
            @if (readonly()) {
              @if (activeLabelPill(); as active) {
                <span class="bmp__mos-label-pill is-on">
                  @if (active.color) {
                    <span class="bmp__mos-label-dot" [style.background]="active.color" aria-hidden="true"></span>
                  }
                  {{ active.label }}
                </span>
              } @else {
                <span class="mos__readonly-empty">{{ 'viewer.book_meeting_panel.more_options.none' | transloco }}</span>
              }
            } @else {
              <div class="bmp__mos-label-pills" role="radiogroup" [attr.aria-label]="'viewer.book_meeting_panel.more_options.meeting_label' | transloco">
                <button
                  type="button"
                  role="radio"
                  class="bmp__mos-label-pill"
                  [class.is-on]="!service.meetingLabel()"
                  [attr.aria-checked]="!service.meetingLabel()"
                  (click)="onMeetingLabelChange('')">
                  {{ 'viewer.book_meeting_panel.more_options.none' | transloco }}
                </button>
                @for (pill of effectiveLabelPills(); track pill.value) {
                  <button
                    type="button"
                    role="radio"
                    class="bmp__mos-label-pill"
                    [class.is-on]="service.meetingLabel() === pill.value"
                    [attr.aria-checked]="service.meetingLabel() === pill.value"
                    (click)="onMeetingLabelChange(pill.value)">
                    @if (pill.color) {
                      <span class="bmp__mos-label-dot" [style.background]="pill.color" aria-hidden="true"></span>
                    }
                    {{ pill.label }}
                  </button>
                }
              </div>
            }
          </div>

          <!-- 2-col toggle grid — RSVP + Guests-invite. -->
          @if (!readonly()) {
            <div class="bmp__mos-toggle-grid">
              <div class="bmp__mos-field mos__field">
                <span class="bmp__mos-field-label mos__label">{{ 'viewer.book_meeting_panel.more_options.require_rsvp' | transloco }}</span>
                <div class="mos__row-compact">
                  <button class="mos__mini-toggle" role="switch" type="button"
                    [attr.aria-checked]="service.responseRequested()"
                    [attr.aria-label]="'viewer.book_meeting_panel.more_options.request_rsvp_responses' | transloco"
                    (click)="service.responseRequested.set(!service.responseRequested())">
                    <span class="mos__mini-thumb" [class.on]="service.responseRequested()"></span>
                  </button>
                  <span class="mos__inline-label">
                    {{ service.responseRequested() ? onLabel() : offLabel() }}
                  </span>
                </div>
              </div>

              <div class="bmp__mos-field mos__field">
                <span class="bmp__mos-field-label mos__label">{{ 'viewer.book_meeting_panel.more_options.guests_can_invite' | transloco }}</span>
                <div class="mos__row-compact">
                  <button class="mos__mini-toggle" role="switch" type="button"
                    [attr.aria-checked]="service.guestsCanInvite()"
                    [attr.aria-label]="'viewer.book_meeting_panel.more_options.allow_guests_invite' | transloco"
                    (click)="service.guestsCanInvite.set(!service.guestsCanInvite())">
                    <span class="mos__mini-thumb" [class.on]="service.guestsCanInvite()"></span>
                  </button>
                  <span class="mos__inline-label">
                    {{ service.guestsCanInvite() ? onLabel() : offLabel() }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Single-row toggle — Auto-release room. -->
            <div class="bmp__mos-field mos__field">
              <span class="bmp__mos-field-label mos__label">{{ 'viewer.book_meeting_panel.more_options.auto_release_room' | transloco }}</span>
              <div class="mos__row-compact">
                <button class="mos__mini-toggle" role="switch" type="button"
                  [attr.aria-checked]="service.autoReleaseRoom()"
                  [attr.aria-label]="'viewer.book_meeting_panel.more_options.auto_release_room_aria' | transloco"
                  (click)="service.autoReleaseRoom.set(!service.autoReleaseRoom())">
                  <span class="mos__mini-thumb" [class.on]="service.autoReleaseRoom()"></span>
                </button>
                <span class="mos__inline-label">{{ 'viewer.book_meeting_panel.more_options.auto_release_hint' | transloco }}</span>
              </div>
            </div>
          }

          <!-- Advanced (collapsed sub-section) — Microsoft Graph fields only
               make sense for calendar-backed rooms; gated to avoid sending
               flags the backend will silently ignore. -->
          @if (service.isCalendarBacked()) {
          <button class="mos__adv-toggle" (click)="advancedOpen.set(!advancedOpen())"
            [attr.aria-expanded]="advancedOpen()">
            <span class="mos__adv-label">{{ 'viewer.book_meeting_panel.more_options.advanced' | transloco }}</span>
            <paper-icon class="mos__adv-chevron" [class.expanded]="advancedOpen()"
              name="chevron-down" [size]="10" aria-hidden="true" />
          </button>
          }

          @if (advancedOpen() && service.isCalendarBacked()) {
            <div class="mos__adv-content">
              <div class="mos__grid">
                <!-- Importance -->
                <div class="mos__field">
                  <span class="mos__label">{{ 'viewer.book_meeting_panel.more_options.importance' | transloco }}</span>
                  @if (readonly()) {
                    <span class="mos__readonly-value">{{ importanceLabel() }}</span>
                  } @else {
                    <div class="mos__pill-group">
                      @for (opt of importanceOptions(); track opt.value) {
                        <button class="mos__pill-btn"
                          [class.active]="service.importance() === opt.value"
                          (click)="service.importance.set(opt.value)">
                          {{ opt.label }}
                        </button>
                      }
                    </div>
                  }
                </div>

                <!-- Show As -->
                <div class="mos__field">
                  <label class="mos__label" for="mos-showas">{{ 'viewer.book_meeting_panel.more_options.show_as' | transloco }}</label>
                  @if (readonly()) {
                    <span class="mos__readonly-value">{{ showAsLabel() }}</span>
                  } @else {
                    <select id="mos-showas" class="mos__select"
                      [value]="service.showAs()"
                      (change)="service.showAs.set($any($event.target).value)">
                      <option value="busy">{{ 'viewer.book_meeting_panel.more_options.show_as_busy' | transloco }}</option>
                      <option value="tentative">{{ 'viewer.book_meeting_panel.more_options.show_as_tentative' | transloco }}</option>
                      <option value="free">{{ 'viewer.book_meeting_panel.more_options.show_as_free' | transloco }}</option>
                      <option value="oof">{{ 'viewer.book_meeting_panel.more_options.show_as_oof' | transloco }}</option>
                      <option value="workingElsewhere">{{ 'viewer.book_meeting_panel.more_options.show_as_working_elsewhere' | transloco }}</option>
                    </select>
                  }
                </div>

                <!-- Reminder -->
                <div class="mos__field">
                  <span class="mos__label">{{ 'viewer.book_meeting_panel.more_options.reminder' | transloco }}</span>
                  @if (readonly()) {
                    <span class="mos__readonly-value">
                      @if (service.isReminderOn()) {
                        {{ reminderLabel() }}
                      } @else {
                        {{ 'viewer.book_meeting_panel.more_options.off' | transloco }}
                      }
                    </span>
                  } @else {
                    <div class="mos__row-compact">
                      <button class="mos__mini-toggle" role="switch"
                        [attr.aria-checked]="service.isReminderOn()"
                        (click)="service.isReminderOn.set(!service.isReminderOn())">
                        <span class="mos__mini-thumb" [class.on]="service.isReminderOn()"></span>
                      </button>
                      @if (service.isReminderOn()) {
                        <select class="mos__select mos__select--sm"
                          [value]="service.reminderMinutes()"
                          (change)="service.reminderMinutes.set(+$any($event.target).value)">
                          <option value="5">{{ 'viewer.book_meeting_panel.more_options.reminder_5_min' | transloco }}</option>
                          <option value="10">{{ 'viewer.book_meeting_panel.more_options.reminder_10_min' | transloco }}</option>
                          <option value="15">{{ 'viewer.book_meeting_panel.more_options.reminder_15_min' | transloco }}</option>
                          <option value="30">{{ 'viewer.book_meeting_panel.more_options.reminder_30_min' | transloco }}</option>
                          <option value="60">{{ 'viewer.book_meeting_panel.more_options.reminder_1_hour' | transloco }}</option>
                        </select>
                      }
                    </div>
                  }
                </div>

                <!-- Response Requested -->
                <div class="mos__field">
                  <span class="mos__label">{{ 'viewer.book_meeting_panel.more_options.request_responses' | transloco }}</span>
                  @if (readonly()) {
                    <span class="mos__readonly-value">
                      {{ service.responseRequested() ? yesLabel() : noLabel() }}
                    </span>
                  } @else {
                    <button class="mos__mini-toggle" role="switch"
                      [attr.aria-checked]="service.responseRequested()"
                      (click)="service.responseRequested.set(!service.responseRequested())">
                      <span class="mos__mini-thumb" [class.on]="service.responseRequested()"></span>
                    </button>
                  }
                </div>
              </div>

              <!-- Allow Time Proposals -->
              <div class="mos__field" style="margin-top: 8px">
                @if (readonly()) {
                  <div class="mos__row-compact">
                    <span class="mos__inline-label">{{ 'viewer.book_meeting_panel.more_options.allow_propose_times_label' | transloco }}</span>
                    <span class="mos__readonly-value">
                      {{ service.allowNewTimeProposals() ? yesLabel() : noLabel() }}
                    </span>
                  </div>
                } @else {
                  <div class="mos__row-compact">
                    <button class="mos__mini-toggle" role="switch"
                      [attr.aria-checked]="service.allowNewTimeProposals()"
                      (click)="service.allowNewTimeProposals.set(!service.allowNewTimeProposals())">
                      <span class="mos__mini-thumb" [class.on]="service.allowNewTimeProposals()"></span>
                    </button>
                    <span class="mos__inline-label">{{ 'viewer.book_meeting_panel.more_options.allow_propose_times' | transloco }}</span>
                  </div>
                }
              </div>

              <!-- Categories -->
              <div class="mos__field" style="margin-top: 10px">
                <span class="mos__label">{{ 'viewer.book_meeting_panel.more_options.categories' | transloco }}</span>
                @if (readonly()) {
                  @if (service.categories().length > 0) {
                    <div class="mos__tags mos__tags--readonly">
                      @for (cat of service.categories(); track cat) {
                        <span class="mos__tag">{{ cat }}</span>
                      }
                    </div>
                  } @else {
                    <span class="mos__readonly-empty">{{ 'viewer.book_meeting_panel.more_options.not_set' | transloco }}</span>
                  }
                } @else {
                  <div class="mos__tags">
                    @for (cat of service.categories(); track cat) {
                      <span class="mos__tag">
                        {{ cat }}
                        <button class="mos__tag-x" (click)="removeCategory(cat)" [attr.aria-label]="'viewer.book_meeting_panel.more_options.remove' | transloco">×</button>
                      </span>
                    }
                    <input class="mos__tag-input" type="text"
                      [placeholder]="'viewer.book_meeting_panel.more_options.add_tag_placeholder' | transloco"
                      (keydown.enter)="addCategory($any($event.target)); $event.preventDefault()" />
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }

    .mos { margin-bottom: 20px; }

    // ── Schedule V2 — More scheduling options (prototype parity) ─────
    .bmp__mos-wrap { margin-bottom: 0; }
    .bmp__mos-trigger {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      width: auto;
      padding: 6px 0;
      background: none;
      border: none;
      font-size: 14px;
      font-weight: 500;
      color: var(--text);
      cursor: pointer;

      &:hover { color: var(--success-strong); }
      &:focus-visible {
        outline: 2px solid var(--brand-focus-ring, var(--color-primary));
        outline-offset: 2px;
        border-radius: var(--radius-sm, 6px);
      }

      @media (prefers-reduced-motion: reduce) { transition: none; }
    }
    .bmp__mos-meta {
      color: var(--text-tertiary);
      font-weight: 400;
      font-size: 11px;
      margin-left: 6px;
    }
    .bmp__mos-body {
      margin-top: 10px;
      padding: 14px;
      border: 1px solid var(--border-subtle);
      border-radius: var(--r, 12px);
      background: transparent;
      display: flex;
      flex-direction: column;
      gap: 14px;

      @media (forced-colors: active) { border: 1px solid CanvasText; }
    }
    .bmp__mos-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
    }
    .bmp__mos-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;
    }
    .bmp__mos-field-label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--text-tertiary);
    }
    .bmp__mos-label-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .bmp__mos-label-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: var(--radius-pill, 999px);
      border: 1px solid var(--divider);
      background: var(--surface-raised, var(--surface-1));
      font-size: 12px;
      color: var(--text-secondary);
      cursor: pointer;
      font-family: inherit;
      transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

      &:focus-visible {
        outline: 2px solid var(--brand-focus-ring, var(--color-primary));
        outline-offset: 1px;
      }
      &:hover {
        color: var(--text);
        border-color: var(--border-med, var(--divider));
      }
      &.is-on {
        background: var(--surface-sunken, var(--surface-2));
        color: var(--text);
        font-weight: 600;
        border-color: var(--border-med, var(--divider));
      }
      @media (prefers-reduced-motion: reduce) { transition: none; }
    }
    .bmp__mos-label-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
    }
    .bmp__mos-toggle-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px 24px;
    }

    // ── Section toggle ─────────────────────────────────
    .mos__toggle {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 0;
      border: none;
      background: transparent;
      cursor: pointer;
      font-family: inherit;
    }

    .mos__toggle-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--text-secondary);
    }

    .mos__warning-pill {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      padding: 2px 8px;
      border-radius: var(--radius-pill, 100px);
      background: rgba(251, 191, 36, 0.12);
      color: var(--bmp-ext-text);
    }

    .mos__chevron {
      margin-left: auto;
      color: var(--text-tertiary);
      transition: transform 200ms ease;

      &.expanded {
        transform: rotate(180deg);
      }
    }

    // ── Content area ───────────────────────────────────
    .mos__content {
      display: flex;
      flex-direction: column;
      gap: 12px;
      overflow: hidden;
      animation: mos-expand 200ms ease forwards;
    }

    @keyframes mos-expand {
      from { opacity: 0; max-height: 0; }
      to   { opacity: 1; max-height: 500px; }
    }

    // ── 2-column grid ──────────────────────────────────
    .mos__grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    // ── Fields ─────────────────────────────────────────
    .mos__field {
      display: flex;
      flex-direction: column;
      gap: 5px;

      &--full {
        grid-column: 1 / -1;
      }
    }

    .mos__label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: var(--text-secondary);

      &--warn {
        color: var(--bmp-ext-text);
      }
    }

    // ── Textarea ───────────────────────────────────────
    .mos__textarea {
      width: 100%;
      padding: 8px 10px;
      background: var(--focus-overlay);
      border: 1px solid var(--divider);
      border-radius: var(--radius-sm, 6px);
      color: var(--text);
      font-size: 11.5px;
      font-family: inherit;
      outline: none;
      resize: vertical;
      transition: border-color 0.2s ease;

      &::placeholder { color: var(--text-tertiary); }
      &:focus { border-color: var(--border-med); }
    }

    // ── Select dropdown ────────────────────────────────
    .mos__select {
      width: 100%;
      padding: 7px 10px;
      background: var(--focus-overlay);
      border: 1px solid var(--divider);
      border-radius: var(--radius-sm, 6px);
      color: var(--text);
      font-size: 11.5px;
      font-family: inherit;
      outline: none;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 16 16' fill='none' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 8px center;
      padding-right: 26px;
      transition: border-color 0.2s ease;

      &:focus { border-color: var(--border-med); }

      &--warn {
        border-color: rgba(251, 191, 36, 0.5);
      }
    }

    // ── Hint text ──────────────────────────────────────
    .mos__hint {
      font-size: 10px;
      line-height: 1.3;
      color: var(--text-tertiary);

      &--warn {
        color: var(--bmp-ext-text);
      }
    }

    // ── Advanced sub-section ─────────────────────────────
    .mos__adv-toggle {
      display: flex; align-items: center; gap: 6px;
      width: 100%; padding: 8px 0 0; margin-top: 10px;
      border-top: 1px solid var(--divider);
      background: none; border-left: none; border-right: none; border-bottom: none;
      color: var(--text-secondary); font: inherit; font-size: 11px; font-weight: 500;
      cursor: pointer;
    }
    .mos__adv-label { flex: 1; text-align: left; }
    .mos__adv-chevron {
      color: var(--text-tertiary); transition: transform 200ms ease;
      &.expanded { transform: rotate(180deg); }
    }
    .mos__adv-content { margin-top: 10px; }

    .mos__pill-group {
      display: inline-flex; padding: 1px; gap: 1px;
      background: var(--focus-overlay);
      border-radius: 100px; border: 1px solid var(--divider);
    }
    .mos__pill-btn {
      padding: 4px 10px; font-size: 10px; font-weight: 600;
      border-radius: 100px; border: none; cursor: pointer;
      background: transparent; color: var(--text-tertiary);
      font: inherit; font-size: 10px; font-weight: 600;
      transition: all 0.15s;
      &.active { background: var(--glass-border); color: var(--text); }
    }

    .mos__row-compact {
      display: flex; align-items: center; gap: 8px;
    }
    .mos__inline-label {
      font-size: 11px; color: var(--text-secondary);
    }

    .mos__mini-toggle {
      width: 30px; height: 17px; border-radius: 100px;
      background: var(--glass-border); border: none;
      position: relative; flex: none; cursor: pointer;
      transition: background 0.2s;
      &[aria-checked="true"] { background: var(--bmp-accent); }
    }
    .mos__mini-thumb {
      position: absolute; top: 1px; left: 2px;
      width: 13px; height: 13px; border-radius: 50%;
      background: var(--text-secondary);
      transition: left 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      &.on { left: 14px; background: var(--surface-1); }
    }

    .mos__select--sm { width: auto; min-width: 80px; }

    /* ── Meeting-label chips ─────────────────────────── */
    .mos__label-chips {
      display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;
    }
    .mos__label-chip {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 12px; font-size: 12px; font-weight: 500;
      color: var(--text-secondary);
      background: var(--focus-overlay);
      border: 1px solid var(--glass-border);
      border-radius: 999px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 500;
      transition: border-color 0.15s, background 0.15s, color 0.15s;

      &:hover { border-color: var(--glass-border-hover); color: var(--text); }
      &.mos__label-chip--selected {
        font-weight: 600;
        color: var(--text);
        border-color: var(--mos-chip-text, var(--color-primary));
        background: var(--mos-chip-bg, var(--focus-overlay));
      }
    }
    .mos__label-chip--coloured {
      background: var(--mos-chip-bg, var(--focus-overlay));
      border-color: var(--mos-chip-border, var(--glass-border));
      color: var(--mos-chip-text, var(--text));
      &.mos__label-chip--selected {
        color: var(--mos-chip-text, var(--text));
      }
    }
    .mos__label-chip-dot {
      width: 8px; height: 8px; border-radius: 50%;
      border: 1px solid currentColor;
      flex: none;
    }

    /* Light-mode swap — the chip variables are pre-derived for both
       themes; no @media needed because the panel publishes both sets. */
    [data-theme="light"] {
      .mos__label-chip--coloured {
        background: var(--mos-chip-bg-light, var(--focus-overlay));
        border-color: var(--mos-chip-border-light, var(--glass-border));
        color: var(--mos-chip-text-light, var(--text));
        &.mos__label-chip--selected { color: var(--mos-chip-text-light, var(--text)); }
      }
    }

    .mos__tags {
      display: flex; flex-wrap: wrap; gap: 4px;
      padding: 6px 8px;
      background: var(--focus-overlay);
      border: 1px solid var(--divider);
      border-radius: 6px; min-height: 32px;
    }
    .mos__tag {
      display: inline-flex; align-items: center; gap: 3px;
      padding: 2px 6px; font-size: 10px; font-weight: 600;
      background: var(--glass-border); color: var(--text);
      border-radius: 100px;
    }
    .mos__tag-x {
      width: 14px; height: 14px; font-size: 12px;
      display: flex; align-items: center; justify-content: center;
      background: none; border: none; color: var(--text-tertiary);
      cursor: pointer; font: inherit; font-size: 12px;
      border-radius: 50%;
      &:hover { color: var(--text); background: var(--hover-overlay); }
    }
    .mos__tag-input {
      flex: 1; min-width: 60px; border: none; background: none;
      outline: none; font: inherit; font-size: 11px;
      color: var(--text); padding: 2px 0;
      &::placeholder { color: var(--text-tertiary); }
    }

    /* ── Read-only renderings ─────────────────────────── */
    .mos__readonly-value {
      font-size: 12px;
      color: var(--text);
      padding: 4px 0;
      line-height: 1.4;
    }

    .mos__readonly-empty {
      font-size: 12px;
      color: var(--text-tertiary);
      font-style: italic;
      padding: 4px 0;
    }

    .mos__readonly-html {
      padding: 6px 0;
      font-size: 12px;
      color: var(--text-secondary);
      line-height: 1.5;

      :first-child { margin-top: 0; }
      :last-child { margin-bottom: 0; }

      p { margin: 0 0 6px; }
      a { color: var(--color-primary); }
    }

    .mos__readonly-chip {
      cursor: default;
      align-self: flex-start;
    }

    .mos__tags--readonly {
      min-height: 0;
      padding: 0;
      background: transparent;
      border: none;
    }

    // ── Reduced motion ─────────────────────────────────
    @media (prefers-reduced-motion: reduce) {
      .mos__chevron { transition: none; }
      .mos__content { animation: none; }
      .mos__textarea,
      .mos__select { transition: none; }
    }
  `],
})
export class MoreOptionsSectionComponent {
  protected readonly service = inject(BookMeetingService);
  private readonly graphql = inject(GraphQLClientService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);
  protected readonly expanded = signal(false);
  protected readonly advancedOpen = signal(false);

  /** Driven by BookMeetingService.mode — replaces every editor field
   *  with a static label/value rendering when 'view'. */
  protected readonly readonly = computed(() => this.service.mode() === 'view');

  protected readonly importanceOptions = computed(() => {
    this.locale.locale();
    return [
      { value: 'low' as const, label: this.transloco.translate('viewer.book_meeting_panel.more_options.importance_low') },
      { value: 'normal' as const, label: this.transloco.translate('viewer.book_meeting_panel.more_options.importance_normal') },
      { value: 'high' as const, label: this.transloco.translate('viewer.book_meeting_panel.more_options.importance_high') },
    ];
  });

  /** Pretty label for the currently-selected showAs value. */
  protected readonly showAsLabel = computed(() => {
    this.locale.locale();
    const map: Record<string, string> = {
      free: this.transloco.translate('viewer.book_meeting_panel.more_options.show_as_free'),
      tentative: this.transloco.translate('viewer.book_meeting_panel.more_options.show_as_tentative'),
      busy: this.transloco.translate('viewer.book_meeting_panel.more_options.show_as_busy'),
      oof: this.transloco.translate('viewer.book_meeting_panel.more_options.show_as_oof'),
      workingElsewhere: this.transloco.translate('viewer.book_meeting_panel.more_options.show_as_working_elsewhere'),
    };
    return map[this.service.showAs()] ?? this.service.showAs();
  });

  /** Pretty label for the currently-selected importance value. */
  protected readonly importanceLabel = computed(() => {
    const opt = this.importanceOptions().find(o => o.value === this.service.importance());
    return opt?.label ?? this.service.importance();
  });

  /** Pretty label for reminder minutes when reminders are enabled. */
  protected readonly reminderLabel = computed(() => {
    this.locale.locale();
    const m = this.service.reminderMinutes();
    if (m >= 60) {
      const h = m / 60;
      return h === 1
        ? this.transloco.translate('viewer.book_meeting_panel.more_options.reminder_1_hour')
        : this.transloco.translate('viewer.book_meeting_panel.more_options.reminder_n_hours', { count: h });
    }
    return this.transloco.translate('viewer.book_meeting_panel.more_options.reminder_n_min', { count: m });
  });

  protected readonly onLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.more_options.on');
  });
  protected readonly offLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.more_options.off');
  });
  protected readonly yesLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.more_options.yes');
  });
  protected readonly noLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.more_options.no');
  });
  protected readonly selectCostCentreLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.more_options.select_cost_centre');
  });
  protected readonly noneLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.more_options.none');
  });

  /** Sanitised description HTML for read-only display — null when empty so
   *  the template can render the "Not set" hint instead. The body comes
   *  from the booking's agenda or the Graph mirror's bodyContent and may
   *  contain rich HTML, so we mirror the meeting-detail-panel pattern. */
  protected readonly sanitizedDescription = computed<SafeHtml | null>(() => {
    const body = this.service.description();
    if (!body || !body.trim()) return null;
    return this.sanitizer.bypassSecurityTrustHtml(body);
  });

  /** The currently-selected MeetingLabelOption (if any) — used by the
   *  read-only renderer to surface a single chip with the right tokens. */
  protected readonly selectedMeetingLabel = computed<MeetingLabelOption | null>(() => {
    const name = this.service.meetingLabel();
    if (!name) return null;
    return this.meetingLabels().find(l => l.name === name) ?? null;
  });

  /** Cost centre values from bookingCapabilities API */
  protected readonly costCentreValues = signal<string[]>([]);
  private readonly costCentreMandatory = signal(false);

  /** Meeting labels authored at the active location. Empty ⇒ the pill row
   *  shows only the always-present "None" choice (j8q.11) — no invented
   *  fallback taxonomy. Cached per locationId so we don't re-fetch each time
   *  the primary room changes inside the same building. */
  protected readonly meetingLabels = signal<MeetingLabelOption[]>([]);
  private _labelsCacheKey: string | null = null;

  /** True when catering is active and no cost centre is selected */
  protected readonly showCostCentreWarning = computed(
    () => (this.service.hasCatering() || this.costCentreMandatory()) && !this.service.costCentre(),
  );

  /** Pill set the LABEL row renders — the location's authored set only
   *  (j8q.11). The prototype's hardcoded 4-label fallback (Important/
   *  Private/Client/Internal) disagreed with `sp-meeting-card`'s own
   *  separate 13-item fallback on what a "label" even was for the same
   *  meeting; deleted rather than coordinated, since `Location.meetingLabels`
   *  is the one real, admin-authored source. Empty means the row is genuinely
   *  hidden (see `meetingLabels`'s own doc), which is the honest answer for
   *  an un-provisioned location. */
  protected readonly effectiveLabelPills = computed<readonly { value: string; label: string; color: string | null }[]>(
    () => this.meetingLabels().map(l => ({ value: l.name, label: l.name, color: l.color })),
  );

  /** Read-only convenience — the currently-selected pill. */
  protected readonly activeLabelPill = computed<{ value: string; label: string; color: string | null } | null>(() => {
    const current = this.service.meetingLabel();
    if (!current) return null;
    return this.effectiveLabelPills().find(p => p.value === current) ?? null;
  });

  /** Trigger meta — "3 set" or "Defaults" — so users can see at a
   *  glance whether the section is doing anything. */
  protected readonly summaryMeta = computed<string>(() => {
    this.locale.locale();
    let count = 0;
    if (this.service.costCentre()) count++;
    if (this.service.meetingLabel()) count++;
    if (this.service.visibility() === 'Private') count++;
    if (!this.service.responseRequested()) count++;
    if (this.service.guestsCanInvite()) count++;
    if (this.service.autoReleaseRoom()) count++;
    return count > 0
      ? this.transloco.translate('viewer.book_meeting_panel.more_options.n_set', { count })
      : this.transloco.translate('viewer.book_meeting_panel.more_options.defaults');
  });

  constructor() {
    // Auto-open the section when catering turns on and cost centre is empty
    effect(() => {
      if (this.service.hasCatering() && !this.service.costCentre()) {
        this.expanded.set(true);
      }
    });

    // Fetch cost centres when primary room changes
    effect(() => {
      const room = this.service.primaryRoom();
      if (room?.spaceId) {
        const start = this.service.startTime();
        const date = start
          ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
          : new Date().toISOString().slice(0, 10);
        this.fetchCapabilities(room.spaceId, date);
      }
    });

    // Fetch the location's meeting-labels list when the booked room's
    // location changes. Cached so revisiting the same building is free.
    effect(() => {
      const room = this.service.primaryRoom();
      if (!room?.locationId) return;
      if (this._labelsCacheKey === room.locationId) return;
      this._labelsCacheKey = room.locationId;
      this.fetchMeetingLabels(room.locationId);
    });
  }

  private async fetchMeetingLabels(locationId: string): Promise<void> {
    try {
      const data = await this.graphql.query<{
        meetingLabels?: { _id: string; name: string; color?: string | null; order?: number | null }[];
      }>(
        `query locationMeetingLabels($locationId: ID!) {
          location(locationId: $locationId) { _id meetingLabels { _id name color order } }
        }`,
        { locationId },
      );
      const raw = Array.isArray(data?.meetingLabels) ? data.meetingLabels : [];
      const labels: MeetingLabelOption[] = raw
        .filter(l => !!l && !!l.name)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(l => ({
          _id: l._id,
          name: l.name,
          color: l.color || null,
          tokens: this.tokensFor(l.color || null),
        }));
      this.meetingLabels.set(labels);
      // If the user previously picked a label that no longer exists for
      // this location, clear the selection so we don't carry stale state.
      const current = this.service.meetingLabel();
      if (current && !labels.some(l => l.name === current)) {
        this.onMeetingLabelChange('');
      } else if (current) {
        // Re-publish the colour so room cards consuming `service.meetingLabelColor`
        // pick up tokens for the new building.
        const match = labels.find(l => l.name === current);
        if (match) this.service.meetingLabelColor?.set?.(match.tokens);
      }
    } catch {
      this.meetingLabels.set([]);
    }
  }

  /** Run an authored hex through the brand-colour pipeline so the chip and
   *  the meeting card stay accessible in both themes. Returns null when
   *  the colour is missing/invalid; chips fall back to the neutral style. */
  private tokensFor(hex: string | null): MeetingLabelTokens | null {
    if (!hex || !/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return null;
    try {
      const t = deriveBrandTokens(hex);
      return {
        dark: t.dark,
        light: t.light,
        bgDark: t.bgDark,
        bgLight: t.bgLight,
        borderDark: t.borderDark,
        borderLight: t.borderLight,
        textOnDark: textOnColor(t.dark),
        textOnLight: textOnColor(t.light),
      };
    } catch {
      return null;
    }
  }

  /** Inline style for a label chip. Uses dark-mode tokens (panel ships
   *  dark-first); the @media (prefers-color-scheme: light) block in styles
   *  swaps to the light tokens via the same custom properties. */
  protected chipStyle(label: MeetingLabelOption): string {
    if (!label.tokens) return '';
    return [
      `--mos-chip-bg: ${label.tokens.bgDark}`,
      `--mos-chip-border: ${label.tokens.borderDark}`,
      `--mos-chip-text: ${label.tokens.dark}`,
      `--mos-chip-bg-light: ${label.tokens.bgLight}`,
      `--mos-chip-border-light: ${label.tokens.borderLight}`,
      `--mos-chip-text-light: ${label.tokens.light}`,
    ].join(';');
  }

  private async fetchCapabilities(spaceId: string, date: string): Promise<void> {
    try {
      const data = await this.graphql.query<any>(BOOKING_CAPABILITIES_QUERY, { spaceId, date }, 'network-only');
      if (data) {
        const values = data.costCentreValues || [];
        const mandatory = data.costCentreMandatory || false;
        this.costCentreValues.set(values);
        this.costCentreMandatory.set(mandatory);

        // Push the recurrence policy up to the service so the
        // date-time-section can hide the Repeat UI when the location
        // policy disallows recurring bookings, cap the stepper at
        // maxRecurringOccurrences and surface the limits inline.
        // Default permissive when every field is missing (legacy
        // capability response) — see applyRecurrenceLimits.
        this.service.applyRecurrenceLimits({
          allowRecurring: typeof data.allowRecurring === 'boolean' ? data.allowRecurring : null,
          canRecur: typeof data.canRecur === 'boolean' ? data.canRecur : null,
          maxRecurringOccurrences: typeof data.maxRecurringOccurrences === 'number' ? data.maxRecurringOccurrences : null,
          maxRecurringDaysAhead: typeof data.maxRecurringDaysAhead === 'number' ? data.maxRecurringDaysAhead : null,
        });

        // Auto-fill if mandatory and only one option
        if (mandatory && values.length === 1 && !this.service.costCentre()) {
          this.service.costCentre.set(values[0]);
        }
      }
    } catch {
      // Fallback: keep empty (shows free-text input)
    }
  }

  /** When the user picks (or clears) a meeting label, replace the previous
   *  label tag inside `categories` with the new one and publish the
   *  derived colour tokens to the service so the meeting card can adopt
   *  them. Free-text category tags the user added by hand are preserved. */
  protected onMeetingLabelChange(value: string): void {
    const previous = this.service.meetingLabel();
    const labelNames = new Set(this.meetingLabels().map(l => l.name));
    this.service.meetingLabel.set(value);
    this.service.categories.update(cats => {
      const filtered = cats.filter(c => c !== previous && !(labelNames.has(c) && c !== value));
      if (value && !filtered.includes(value)) filtered.push(value);
      return filtered;
    });
    const match = this.meetingLabels().find(l => l.name === value);
    this.service.meetingLabelColor?.set?.(match?.tokens ?? null);
  }

  /** Sync visibility dropdown to both visibility and isPrivate signals */
  protected onVisibilityChange(value: 'Public' | 'Private'): void {
    this.service.visibility.set(value);
    this.service.isPrivate.set(value === 'Private');
  }

  protected addCategory(input: HTMLInputElement): void {
    const val = input.value.trim();
    if (val && !this.service.categories().includes(val)) {
      this.service.categories.update(cats => [...cats, val]);
    }
    input.value = '';
  }

  protected removeCategory(cat: string): void {
    this.service.categories.update(cats => cats.filter(c => c !== cat));
  }
}
